import { useEffect, useState } from 'react';
import { Box, Button, Divider, Grid, LinearProgress, Paper, Slider, Snackbar, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

//import { TicketsService } from '../../shared/services/api/ticket/TicketsService';
import { VTextField, VForm, useVForm, IVFormErrors } from '../../shared/forms';
import { FerramentasDeDetalhe } from '../../shared/components';
import { LayoutBaseDePagina } from '../../shared/layouts';
import { PartnerSelect } from './components/ListParceirosComponent';
import { TicketsServiceAPI } from '../../shared/services/api/ticket/TicketsService';
import { useAuth } from '../../shared/contexts';
import { IBillingInformation, IFormTicketData } from '../../shared/types/types';
import ConfirmDeleteDialog from './components/ConfirmDeleteDialog';
import BillingDialog from './components/BillingDialog';


const formValidationSchema: yup.Schema<IFormTicketData> = yup.object().shape({
  customer: yup.object().shape({
    id: yup
      .string()
      .required('O campo ID do cliente é obrigatório.'),
    nome_customer: yup
      .string()
      .required('O campo Nome do cliente é obrigatório.'),
  }).test('is-selected', 'O campo Customer deve ter um cliente selecionado.', (value) => !!value?.id),

});

export const DetalheDeTickets: React.FC = () => {
  const { formRef, save, saveAndClose, isSaveAndClose } = useVForm();
  const { id = 'nova' } = useParams<'id'>();
  const navigate = useNavigate();
  const { getUser } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [nome, setNome] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [passConnection, setPassConnection] = useState('');
  const [sliderValue, setSliderValue] = useState(24);
  const [stats, setStats] = useState<string>('CREATED');
  const [type, setType] = useState<string>('Normal');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [billingData, setBillingData] = useState<IBillingInformation>();
  const [isFetching, setIsFetching] = useState(false);

  const marks = [{ value: 24, label: '1 Dia' }, { value: 48, label: '2 Dias' }, { value: 72, label: '3 Dias' }, { value: 96, label: '4 Dias' }];

  function valuetext(value: number) {
    return `${value}°C`;
  }

  useEffect(() => {
    if (id !== 'nova' && !isFetching) { // Verifica se já está sendo realizado um fetch
      setIsFetching(true); // Marca como "em andamento"
      setIsLoading(true);

      TicketsServiceAPI.getById(getUser(), id)
        .then((result) => {
          setIsFetching(false); // Marca como "concluído"
          setIsLoading(false);

          if (result instanceof Error) {
            setSnackbarMessage(result.message);
            navigate('/tickets');
          } else {
            formRef.current?.setData({ ...result.data });
            setSliderValue(result.data?.time_connection || 0);
            setPassConnection(result.data?.pass_connection || '');
            setStats(result.data?.stats || '');
            setType(result.data?.type || '');


            // Ajuste para exibir o diálogo apenas se stats não for 'PAID' ou 'FREE'
            const statsTrimmed = (result.data?.stats ?? '').trim(); // Garante que será string
            if (!['PAID', 'FREE'].includes(statsTrimmed) && result.data?.type === 'Normal') {
              //setBillingData(result.data.billing_id);
              //setIsDialogOpen(true);
              if (result.data.billing_id) {
                setBillingData(result.data.billing_id);
                setIsDialogOpen(true);
              }
            }

          }
        })
        .catch(() => {
          setIsFetching(false); // Marca como "concluído" mesmo em caso de erro
          setIsLoading(false);
        });
    }
  }, [id]); // Remove outras dependências irrelevantes

  const handleSave = (dados: IFormTicketData) => {
    console.log('Dados enviados:', dados); // Log para depuração
    formValidationSchema.
      validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === 'nova') {
          TicketsServiceAPI
            .create(getUser(), { ...dadosValidados, time_connection: sliderValue, pass_connection: passConnection })
            .then((result) => {
              setIsLoading(false);
              console.log("AJUTAR APOS SALVAR")
              if (result instanceof Error) {
                setSnackbarMessage(result.message);
              } else {
                if (isSaveAndClose()) {
                  navigate('/tickets');
                } else {
                  navigate(`/tickets/detalhe/${result.data.id}`);
                }
              }
            });
        } else {

          TicketsServiceAPI
            .updateById(getUser(), id, { ...dadosValidados })
            .then((result) => {
              setIsLoading(false);

              if (result instanceof Error) {
                setSnackbarMessage(result.message);
              } else {
                if (isSaveAndClose()) {
                  navigate('/tickets');
                }
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};

        errors.inner.forEach(error => {
          if (!error.path) return;

          validationErrors[error.path] = error.message;
        });

        formRef.current?.setErrors(validationErrors);
      });
  };

  const handleDelete = () => {
    setOpenDialog(true); // Abre o diálogo de confirmação
  };

  const handleConfirmDelete = () => {
    //if (confirm('Realmente deseja apagar?')) {
    TicketsServiceAPI.deleteById(getUser(), id)
      .then(result => {
        if (result instanceof Error) {
          setSnackbarMessage(result.message);
        } else {
          setSnackbarMessage('Registro apagado com sucesso!');
          navigate('/tickets');
        }
      });
    //}
  };

  // Função para `onPointerEnterCapture`
  const handlePointerEnterCapture = (event: React.PointerEvent) => {
    console.log("Pointer entered:", event);
  };

  // Função para `onPointerLeaveCapture`
  const handlePointerLeaveCapture = (event: React.PointerEvent) => {
    console.log("Pointer left:", event);
  };

  // Propriedade para `placeholder`
  const placeholderText = "Digite aqui..."; // Substitua por algo útil ou `undefined`

  return (
    <LayoutBaseDePagina
      titulo={id === 'nova' ? 'Nova pessoa' : nome}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Nova'
          mostrarBotaoSalvarEFechar={id === 'nova'}
          mostrarBotaoSalvar={id === 'nova'}
          mostrarBotaoNovo={id !== 'nova'}
          mostrarBotaoApagar={id !== 'nova'}
          mostrarBotaoSalvarCarregando={isLoading}

          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate('/tickets')}
          aoClicarEmApagar={handleDelete}
          aoClicarEmNovo={() => navigate('/tickets/detalhe/nova')}
        />
      }
    >
      <ConfirmDeleteDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={handleConfirmDelete}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
      <VForm ref={formRef} onSubmit={handleSave} placeholder={placeholderText} onPointerEnterCapture={handlePointerEnterCapture} onPointerLeaveCapture={handlePointerLeaveCapture}>
        <Box margin={2} padding={3} component={Paper} variant="outlined">

          <Grid container direction="column" padding={2} spacing={2} width="100%">

            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}

            <Grid item>
              <Typography variant='h6'>Tickets</Typography>
            </Grid>
            <Grid item xs={12}>
              <PartnerSelect isExternalLoading={isLoading} isDisable={id !== 'nova'} />
            </Grid>

            <Grid container item spacing={2}>
              {type === 'Normal' && ['CREATED', 'AWAITING PAYMENT', 'PAYMENT ERROR'].includes(stats?.trim()) ? (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      name='user_connection'
                      disabled
                      value="****" // Exibe '****' para outros status
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      name='user_connection'
                      disabled
                      value="****" // Exibe '****' para outros status
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={6}>
                    <VTextField
                      fullWidth
                      name='user_connection'
                      label='Usuário'
                      disabled
                      value={nome} // Exibe o nome do usuário
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      defaultValue={passConnection}
                      fullWidth
                      name='pass_connection'
                      disabled
                      label='Senha'
                      value={passConnection} // O campo "Senha" reflete o estado atualizado
                    />
                  </Grid>



                </>
              )}

            </Grid>

            <Grid container item xs={12} >
              <Grid item xs={12} sm={2}>  {/* Ajuste para ocupar 100% em telas pequenas e 16% em telas maiores */}
                <Typography>Horas de Wi-FI</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>  {/* Ajuste para 100% em telas menores e 66% em telas maiores */}
                <Slider
                  name="time_connection"
                  aria-label="Horas de Wi-FI"
                  value={sliderValue}  // Valor numérico do Slider
                  getAriaValueText={valuetext}
                  step={12}
                  valueLabelDisplay="auto"
                  marks={marks}
                  disabled={isLoading || id !== 'nova'}
                  onChange={(e, newValue) => {
                    if (typeof newValue === 'number') {
                      setSliderValue(newValue);
                    }
                  }}
                />
              </Grid>
              <Grid container item spacing={2}>
                <Grid item sm={6}>
                  <VTextField
                    fullWidth
                    name='stats'
                    label='Status'
                    disabled
                    value={stats} // Exibe o nome do usuário
                  />
                </Grid>
                <Grid item sm={6}>
                  <TextField
                    defaultValue={passConnection}
                    fullWidth
                    name='type'
                    disabled
                    label='Typo de Bilhete'
                    value={type} // O campo "Senha" reflete o estado atualizado
                  />
                </Grid>
              </Grid>

              {id === 'nova' && (
                <>
                  <Grid item xs={12}>
                    <Divider style={{ margin: '20px ' }} /> {/* Linha divisória */}
                  </Grid>
                  <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-start', gap: '16px' }}>
                    <Button
                      disabled={isLoading}
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        formRef.current?.submitForm();
                      }}
                    >
                      Ticket
                    </Button>
                    <Button
                      disabled={isLoading}
                      variant="contained"
                      color="primary"
                      onClick={async () => {
                        console.log('Antes de atualizar o campo type'); // Log para verificar
                        await formRef.current?.setFieldValue('type', 'Temporario');
                        console.log('Depois de atualizar o campo type'); // Log para verificar
                        formRef.current?.submitForm(); // Envia o formulário
                      }}
                    >
                      Ticket Temporário
                    </Button>
                    <Button
                      disabled={isLoading}
                      variant="contained"
                      color="warning"
                      onClick={async () => {
                        console.log('Antes de atualizar o campo type'); // Log para verificar
                        await formRef.current?.setFieldValue('type', 'Manual'); // Atualiza o campo 'type'
                        await new Promise(resolve => setTimeout(resolve, 0)); // Aguarda a atualização do valor

                        console.log('Depois de atualizar o campo type'); // Log para verificar

                        // Obtém os dados atualizados do formulário
                        const formData = formRef.current?.getData();
                        console.log('Dados do formulário após atualização:', formData); // Log com os dados atualizados
                      }}
                    >
                      Ticket Manual
                    </Button>

                  </Grid>
                </>
              )}
            </Grid>
          </Grid>

        </Box>
      </VForm>
      {billingData && (
        <BillingDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          billingInfo={billingData}
        />
      )}

    </LayoutBaseDePagina >
  );
};
